import * as React from 'react';
import TextField, { TextFieldProps } from '@mui/material/TextField';
import { useTheme } from '@mui/material/styles';
import type { SxProps, Theme } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import ReactDatePicker from '../../react-datepicker-compat';
import 'react-datepicker/dist/react-datepicker.css';
import './scss/react-datepicker-fixed.scss';
import i18n from '../../i18n';

export default function MaterialUIPickers(props: MaterialUIPickersProps) {

    const [value, setValue] = React.useState<Date | null>(null);

    React.useEffect(() => {
        if (props.value !== undefined) {
            const v = props.value instanceof Date || moment.isMoment(props.value)
                ? props.value
                : moment(props.value, moment.HTML5_FMT.DATETIME_LOCAL_MS).toDate()
            setValue(props.value ? v : null)
        }
    }, [props.value])


    const handleChange = (newValue: Date | null) => {
        props?.handleChange?.(newValue);

    };

    // 完整选择时间值后的回调
    const onClosePicker = (newValue: Date | null) => {
        props?.onClosePicker?.(newValue);
    }

    return (
        <LocalizationProvider dateAdapter={AdapterMoment}>
            <PickerCom
                {...{
                    value,
                    handleChange,
                    onClosePicker,
                    pickerType: props.pickerType,
                    textFieldProps: props.textFieldProps,
                    rangeValue: props.rangeValue,
                    rangeHandleChangePicker: props.rangeHandleChangePicker,
                    fullWidth: props.fullWidth,
                    minDate: props.minDate,
                    maxDate: props.maxDate,
                    filterTime: props.filterTime
                }}
            />
        </LocalizationProvider>
    );
}
interface PickerProps {
    label?: React.ReactNode;
    end?: Date | null;
    start?: Date | null;
    pickertype?: PickerType;
    changecallback?: (params: { dates?: [Date, Date], error?: boolean }) => void;
}
// eslint-disable-next-line react/display-name
const CustomInput = React.forwardRef((props: PickerProps, ref) => {
    const { label, end, start, pickertype, changecallback, ...otherProps } = props;
    const formatRule = pickertype === 'DATE_RANGE' ? 'YYYY/MM/DD' : 'YYYY/MM/DD HH:mm';

    const [value, setValue] = React.useState('');
    const [isError, setIsError] = React.useState(false);
    React.useEffect(() => {
        setIsError(false);
        const startDate = start ? moment(start).format(formatRule) : '';
        const endDate = end ? ` - ${moment(end).format(formatRule)}` : null;
        setValue(`${startDate}${endDate !== null ? endDate : ''}`);
    }, [start, end])

    const checkDate = (date: string) => {
        try {
            if (date.length !== formatRule.length || moment(date).format(formatRule) === 'Invalid date') {
                setIsError(true);
                return false
            } else {
                setIsError(false);
                return moment(date).toDate();
            }
        } catch (error) {
            setIsError(true);
            return false
        }
    }
    return <TextField
        inputRef={ref}
        label={label || ''}
        {...otherProps}
        fullWidth
        size='small'
        value={value}
        error={isError}
        helperText={isError ? i18n.t('general_text_invalid_date') : undefined}
        onChange={(v) => {
            const tValue = v.target.value;
            setValue(v.target.value);
            if (tValue && tValue.includes(' - ')) {
                const reange = tValue.split(' - ');
                const startDate = checkDate(reange[0]);
                const endDate = checkDate(reange[1]);
                if (startDate instanceof Date && endDate instanceof Date) {
                    if (moment(startDate).isAfter(endDate)) {
                        setIsError(true);
                        changecallback?.({ error: true });
                    } else {
                        changecallback?.({ dates: [startDate, endDate] });
                    }
                } else {
                    changecallback?.({ error: true });
                }
            }

        }}
    />;
})

function PickerCom(props: MaterialUIPickersProps) {
    const theme = useTheme();
    const { value, pickerType, handleChange, onClosePicker, textFieldProps, rangeValue, rangeHandleChangePicker, minDate, maxDate, filterTime } = props;
    const { inputProps: tfUserInputProps, ...tfRest } = textFieldProps ?? {};
    /** 单值日期/时间与 TextField 透传（支持 `InputProps` 装饰等） */
    const singlePickerTextField = (
        <TextField
            variant="outlined"
            fullWidth
            {...tfRest}
            inputProps={{ readOnly: true, ...tfUserInputProps }}
        />
    );
    const popperModifiers = [
        {
            name: 'offset',
            options: {
                offset: [5, 10],
            },
        },
        {
            name: 'preventOverflow',
            options: {
                rootBoundary: 'viewport',
                tether: false,
                altAxis: true,
            },
        },
    ];
    /** 与 `TIME_RANGE_COMPACT` 内单框一致：窄宽、tabular 数字、hover/focus 描边 */
    const buildMergedTimeCompactSx = (tfSx: SxProps<Theme> | undefined): SxProps<Theme> => {
        const primary = theme.palette.primary.main;
        const compactOutlineSx = {
            width: 'calc(4ch + 28px)',
            maxWidth: 'calc(4ch + 28px)',
            flexShrink: 0,
            '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                cursor: 'pointer',
                backgroundColor: 'transparent',
                '&.Mui-disabled': {
                    cursor: 'not-allowed',
                },
                '& fieldset': { border: 'none' },
                '& .MuiOutlinedInput-notchedOutline': {
                    borderWidth: 1,
                    borderStyle: 'solid',
                    borderColor: 'transparent',
                },
                '&:hover:not(.Mui-disabled):not(.Mui-focused) .MuiOutlinedInput-notchedOutline': {
                    borderWidth: 1,
                    borderColor: primary,
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderWidth: 2,
                    borderColor: primary,
                },
            },
            '& .MuiOutlinedInput-input': {
                fontVariantNumeric: 'tabular-nums',
                textAlign: 'center',
                paddingLeft: '0px',
                paddingRight: '0px',
                cursor: 'pointer',
                '&.Mui-disabled': {
                    cursor: 'not-allowed',
                },
            },
        };
        return tfSx !== undefined ? ([compactOutlineSx, tfSx] as SxProps<Theme>) : compactOutlineSx;
    };
    switch (pickerType) {
        case 'DATE':
            return <ReactDatePicker
                showMonthDropdown
                showYearDropdown
                selected={value as Date | null | undefined}
                dateFormat='yyyy/MM/dd'
                onChange={handleChange}
                customInput={singlePickerTextField}
                onCalendarClose={() => onClosePicker?.(value as Date | null)}
                disabled={textFieldProps?.disabled}
                minDate={minDate}
                maxDate={maxDate}
                popperModifiers={popperModifiers}
                scrollableYearDropdown
            />

        case 'TIME':
            return <ReactDatePicker
                showTimeSelect
                showTimeSelectOnly
                selected={value as Date | null | undefined}
                timeIntervals={30}
                timeFormat='HH:mm'
                dateFormat='HH:mm'
                onChange={handleChange}
                customInput={singlePickerTextField}
                onCalendarClose={() => onClosePicker?.(value as Date | null)}
                disabled={textFieldProps?.disabled}
                minDate={minDate}
                maxDate={maxDate}
                popperModifiers={popperModifiers}
            />

        /** 单时间紧凑：样式与 `TIME_RANGE_COMPACT` 单侧相同 */
        case 'TIME_COMPACT': {
            const tfCompact = { ...(textFieldProps ?? {}) } as TextFieldProps;
            delete tfCompact.label;
            const {
                sx: tfSx,
                className: tfClassName,
                size: tfSize,
                disabled: tfDisabled,
                inputProps: tfInputProps,
                ...restTextField
            } = tfCompact;
            const mergedSx = buildMergedTimeCompactSx(tfSx);
            return (
                <ReactDatePicker
                    showTimeSelect
                    showTimeSelectOnly
                    selected={value as Date | null | undefined}
                    timeIntervals={10}
                    timeFormat="HH:mm"
                    dateFormat="HH:mm"
                    onChange={handleChange}
                    customInput={
                        <TextField
                            variant="outlined"
                            {...restTextField}
                            fullWidth={false}
                            inputProps={{ readOnly: true, ...tfInputProps }}
                            size={tfSize ?? 'small'}
                            className={tfClassName}
                            sx={mergedSx}
                        />
                    }
                    disabled={tfDisabled ?? textFieldProps?.disabled}
                    onCalendarClose={() => onClosePicker?.(value as Date | null)}
                    minDate={minDate}
                    maxDate={maxDate}
                    filterTime={filterTime}
                    popperModifiers={popperModifiers}
                />
            );
        }

        case 'DATE_TIME':
            return <ReactDatePicker
                showTimeSelect
                showMonthDropdown
                showYearDropdown
                selected={value as Date | null | undefined}
                timeIntervals={30}
                timeFormat='HH:mm'
                dateFormat='MM/dd/yyyy HH:mm'
                onChange={handleChange}
                customInput={singlePickerTextField}
                onCalendarClose={() => onClosePicker?.(value as Date | null)}
                disabled={textFieldProps?.disabled}
                minDate={minDate}
                maxDate={maxDate}
                popperModifiers={popperModifiers}
            />
        case 'DATE_TIME_RANGE':
        case 'DATE_RANGE': {
            const startDateRange = rangeValue?.[0];
            const endDateRange = rangeValue?.[1];
            return <ReactDatePicker
                endDate={endDateRange}
                selected={endDateRange || startDateRange}
                startDate={startDateRange}
                selectsRange
                showTimeSelect={pickerType === 'DATE_TIME_RANGE'}
                selectsStart
                showYearDropdown
                timeFormat='HH:mm'
                minDate={minDate}
                maxDate={maxDate}
                filterTime={filterTime}
                onChange={(dates) => {
                    if (dates instanceof Array) {
                        rangeHandleChangePicker?.(dates as [Date | null, Date | null]);
                    } else {
                        const newStart = startDateRange ? new Date(startDateRange) : null;
                        const newEnd = endDateRange ? new Date(endDateRange) : null;
                        const hour = (dates as Date).getHours();
                        const min = (dates as Date).getMinutes();
                        // $ 交互流程，1. 选择开始日期，时间点，2.再选结束日期，时间点；
                        if (newEnd) {
                            newEnd?.setHours(hour);
                            newEnd?.setMinutes(min);
                        } else {
                            newStart?.setHours(hour);
                            newStart?.setMinutes(min);
                        }
                        rangeHandleChangePicker?.([newStart, newEnd]);
                    }
                }}
                shouldCloseOnSelect={false}
                customInput={
                    <CustomInput label={textFieldProps?.label} start={startDateRange} end={endDateRange} pickertype={pickerType}
                        changecallback={(params) => {
                            if (params.dates instanceof Array) {
                                rangeHandleChangePicker?.(params.dates);
                            } else {
                                rangeHandleChangePicker?.(undefined, true);
                            }
                        }} />
                }
                disabled={textFieldProps?.disabled}
                popperModifiers={popperModifiers}
            />
        }
        case 'TIME_RANGE': {
            const startTimeRange = rangeValue?.[0] || null;
            const endTimeRange = rangeValue?.[1] || null;
            // 判断endTime是否超过00：00
            const isOver: boolean = moment(endTimeRange).hour() === 0 && moment(endTimeRange).minute() > 0;
            return <div className='time-range-box FCC'>
                <ReactDatePicker
                    showTimeSelect
                    showTimeSelectOnly
                    selected={startTimeRange as Date | null | undefined}
                    timeIntervals={10}
                    timeFormat='HH:mm'
                    dateFormat='HH:mm'
                    onChange={(value) => {
                        rangeHandleChangePicker?.([value, endTimeRange]);
                    }}
                    customInput={<TextField inputProps={{ readOnly: true }} fullWidth label={i18n.t('business_analysis_page_text_start_time')} size={textFieldProps?.size} />}
                    disabled={textFieldProps?.disabled}
                    minDate={minDate}
                    maxDate={maxDate}
                    popperModifiers={popperModifiers}
                />
                <span> - </span>
                <ReactDatePicker
                    showTimeSelect
                    showTimeSelectOnly
                    selected={endTimeRange as Date | null | undefined}
                    timeIntervals={10}
                    timeFormat={'HH:mm'}
                    dateFormat={isOver ? 'HH:mm' : 'kk:mm'}
                    onChange={(value) => {
                        rangeHandleChangePicker?.([startTimeRange, value]);
                    }}
                    customInput={<TextField inputProps={{ readOnly: true }} fullWidth label={i18n.t('business_analysis_page_text_end_time')} size={textFieldProps?.size} />}
                    disabled={textFieldProps?.disabled}
                    minDate={minDate}
                    maxDate={maxDate}
                    popperModifiers={popperModifiers}
                />
            </div>
        }
        /** 时间范围：无 label、默认无边框，hover 底纹，聚焦时描边（逻辑同 TIME_RANGE） */
        case 'TIME_RANGE_COMPACT': {
            const startTimeRange = rangeValue?.[0] || null;
            const endTimeRange = rangeValue?.[1] || null;
            const isOver: boolean = moment(endTimeRange).hour() === 0 && moment(endTimeRange).minute() > 0;
            const tfCompact = { ...(textFieldProps ?? {}) } as TextFieldProps;
            delete tfCompact.label;
            const {
                sx: tfSx,
                className: tfClassName,
                size: tfSize,
                disabled: tfDisabled,
                inputProps: tfInputProps,
                ...restTextField
            } = tfCompact;
            const mergedSx = buildMergedTimeCompactSx(tfSx);
            return (
                <div className="time-range-box time-range-box--compact">
                    <ReactDatePicker
                        showTimeSelect
                        showTimeSelectOnly
                        selected={startTimeRange as Date | null | undefined}
                        timeIntervals={10}
                        timeFormat="HH:mm"
                        dateFormat="HH:mm"
                        onChange={(v) => {
                            rangeHandleChangePicker?.([v, endTimeRange]);
                        }}
                        customInput={
                            <TextField
                                variant="outlined"
                                {...restTextField}
                                fullWidth={false}
                                inputProps={{ readOnly: true, ...tfInputProps }}
                                size={tfSize ?? 'small'}
                                className={tfClassName}
                                sx={mergedSx}
                            />
                        }
                        disabled={tfDisabled ?? textFieldProps?.disabled}
                        minDate={minDate}
                        maxDate={maxDate}
                        popperModifiers={popperModifiers}
                    />
                    <span>-</span>
                    <ReactDatePicker
                        showTimeSelect
                        showTimeSelectOnly
                        selected={endTimeRange as Date | null | undefined}
                        timeIntervals={10}
                        timeFormat="HH:mm"
                        dateFormat={isOver ? 'HH:mm' : 'kk:mm'}
                        onChange={(v) => {
                            rangeHandleChangePicker?.([startTimeRange, v]);
                        }}
                        customInput={
                            <TextField
                                variant="outlined"
                                {...restTextField}
                                fullWidth={false}
                                inputProps={{ readOnly: true, ...tfInputProps }}
                                size={tfSize ?? 'small'}
                                className={tfClassName}
                                sx={mergedSx}
                            />
                        }
                        disabled={tfDisabled ?? textFieldProps?.disabled}
                        minDate={minDate}
                        maxDate={maxDate}
                        popperModifiers={popperModifiers}
                    />
                </div>
            );
        }
        default:
            return <></>
    }
}

export type PickerType = 'DATE' | 'TIME' | 'TIME_COMPACT' | 'DATE_TIME' | 'DATE_TIME_RANGE' | 'TIME_RANGE' | 'TIME_RANGE_COMPACT' | 'DATE_RANGE';

export type MaterialUIPickersProps = {
    pickerType?: PickerType;
    value?: Date | null;
    rangeValue?: [Date | null, Date | null];
    handleChange: (value: Date | null) => void;
    onClosePicker?: (value: Date | null) => void;
    textFieldProps?: TextFieldProps;
    rangeHandleChangePicker?: (value?: [Date | null, Date | null], error?: boolean) => void;
    fullWidth?: boolean;
    minDate?: Date | null;
    maxDate?: Date | null;
    filterTime?(date: Date): boolean;
}